import ProjectAcceptanceReportData from '@/blocks/project/ProjectAcceptanceReportData';
import ProjectMeasurementReportData from '@/blocks/project/ProjectMeasurementReportData';
import ProjectServiceReportData from '@/blocks/project/ProjectServiceReportData';
import ProjectQualityReportData from '@/blocks/project/ProjectQualityReportData';
import ProjectSalesReportData from '@/blocks/project/ProjectSalesReportData';
import {
  ACCEPTANCE_VERIFICATION_TYPES,
  STATE_TYPE_BY_ACCEPTANCE_VERIFICATION_TYPE,
} from '@/blocks/project/projectAcceptanceVerificationTypes';
import {
  MEASUREMENT_VERIFICATION_TYPES,
  STATE_TYPE_BY_MEASUREMENT_VERIFICATION_TYPE,
} from '@/blocks/project/projectMeasurementVerificationTypes';
import {
  SERVICE_VERIFICATION_TYPES,
  STATE_TYPE_BY_SERVICE_VERIFICATION_TYPE,
} from '@/blocks/project/projectServiceVerificationTypes';
import {
  PROJECT_ACCEPTANCE_REPORT_APPROVAL_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_BUILD_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_DATA_BLOCK,
  PROJECT_ACCEPTANCE_REPORT_TAB_GROUP,
  PROJECT_ACCEPTANCE_REPORT_VERIFICATION_BLOCK,
  PROJECT_MEASUREMENT_REPORT_APPROVAL_BLOCK,
  PROJECT_MEASUREMENT_REPORT_BUILD_BLOCK,
  PROJECT_MEASUREMENT_REPORT_DATA_BLOCK,
  PROJECT_MEASUREMENT_REPORT_TAB_GROUP,
  PROJECT_MEASUREMENT_REPORT_VERIFICATION_BLOCK,
  PROJECT_SERVICE_REPORT_APPROVAL_BLOCK,
  PROJECT_SERVICE_REPORT_BUILD_BLOCK,
  PROJECT_SERVICE_REPORT_DATA_BLOCK,
  PROJECT_SERVICE_REPORT_TAB_GROUP,
  PROJECT_SERVICE_REPORT_VERIFICATION_BLOCK,
  PROJECT_QUALITY_REPORT_DATA_BLOCK,
  PROJECT_QUALITY_REPORT_TAB_GROUP,
  PROJECT_SALES_REPORT_BUILD_BLOCK,
  PROJECT_SALES_REPORT_DATA_BLOCK,
  PROJECT_SALES_REPORT_TAB_GROUP,
} from '@/navigation/accessNodes';

/**
 * The protocol tabs of the project detail (see feature page projekt-protokolle), one config each:
 * `ProjectReportTab` renders a section per entry (`build`, `verification`, `approval`, `data`;
 * a protocol leaves out what it does not have), and the generic blocks read their fields, store
 * actions and labels from their entry. Data only, except `data.dataComponent`, the data block with
 * the cards of the protocol. A data block never imports this file (cycle).
 *
 * `typeArgument` is the name of the state argument of the protocol's state actions; a protocol
 * with a single state has none, and its actions take no type.
 */

/** Freezes the protocol and its section entries; the data component itself stays as it is. */
const freezeProtocol = (protocol) =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(protocol).map(([key, value]) => [
        key,
        typeof value === 'object' ? Object.freeze(value) : value,
      ])
    )
  );

export const MEASUREMENT_REPORT = freezeProtocol({
  id: 'measurement-report',
  groupId: PROJECT_MEASUREMENT_REPORT_TAB_GROUP,
  build: {
    blockId: PROJECT_MEASUREMENT_REPORT_BUILD_BLOCK,
    blockField: 'measurementReportBuild',
    labelKey: 'generate_report',
    pdfField: 'measurementReportAttachments',
    pdfBuildStateField: 'measurementReportAttachmentsBuildState',
    zipAttachmentType: 'measurementReportImageAttachments',
    zipField: 'measurementReportImageAttachmentsZip',
    zipLabelKey: 'measurementReportImageAttachments',
    printRoute: 'MeasurementPrintPage',
    buildAction: 'buildMeasurementReport',
    reloadAction: 'fetchProjectMeasurementReportBuildBlock',
    hidesDownloadsForGuests: false,
  },
  verification: {
    blockId: PROJECT_MEASUREMENT_REPORT_VERIFICATION_BLOCK,
    blockField: 'measurementReportVerification',
    labelKey: 'verification',
    types: MEASUREMENT_VERIFICATION_TYPES,
    stateTypeByType: STATE_TYPE_BY_MEASUREMENT_VERIFICATION_TYPE,
    /** Deprecated overall state, shown read-only when set. */
    overallStateType: 'measurementVerificationState',
    changeStateAction: 'updateProjectMeasurementVerificationState',
    typeArgument: 'side',
  },
  approval: {
    blockId: PROJECT_MEASUREMENT_REPORT_APPROVAL_BLOCK,
    blockField: 'measurementReportApproval',
    labelKey: 'approvement',
    saveLabelKey: 'project_measurement_report_approval',
    stateType: 'measurementApprovementVerificationState',
    note: { parentPath: 'measurement', field: 'measurementApprovementNote' },
    noteLabelKey: 'measurementApprovementNote',
    changeStateAction: 'updateProjectMeasurementApprovalState',
    saveAction: 'updateProjectMeasurementApproval',
  },
  data: {
    blockId: PROJECT_MEASUREMENT_REPORT_DATA_BLOCK,
    blockField: 'measurementReportData',
    labelKey: 'collected_data',
    dataComponent: ProjectMeasurementReportData,
    requestSectionNumber: 4,
    requestAction: 'requestProjectMeasurementVerification',
  },
});

export const ACCEPTANCE_REPORT = freezeProtocol({
  id: 'acceptance-report',
  groupId: PROJECT_ACCEPTANCE_REPORT_TAB_GROUP,
  build: {
    blockId: PROJECT_ACCEPTANCE_REPORT_BUILD_BLOCK,
    blockField: 'acceptanceReportBuild',
    labelKey: 'generate_report',
    pdfField: 'reportAttachments',
    pdfBuildStateField: 'reportAttachmentsBuildState',
    zipAttachmentType: 'acceptanceReportImageAttachments',
    zipField: 'acceptanceReportImageAttachmentsZip',
    zipLabelKey: 'acceptanceReportImageAttachments',
    printRoute: 'ReportPrintPage',
    buildAction: 'buildReport',
    reloadAction: 'fetchProjectAcceptanceReportBuildBlock',
    hidesDownloadsForGuests: true,
  },
  verification: {
    blockId: PROJECT_ACCEPTANCE_REPORT_VERIFICATION_BLOCK,
    blockField: 'acceptanceReportVerification',
    labelKey: 'verification',
    types: ACCEPTANCE_VERIFICATION_TYPES,
    stateTypeByType: STATE_TYPE_BY_ACCEPTANCE_VERIFICATION_TYPE,
    changeStateAction: 'updateProjectAcceptanceVerificationState',
    typeArgument: 'type',
  },
  approval: {
    blockId: PROJECT_ACCEPTANCE_REPORT_APPROVAL_BLOCK,
    blockField: 'acceptanceReportApproval',
    labelKey: 'approvement',
    saveLabelKey: 'project_acceptance_report_approval',
    stateType: 'acceptanceApprovementVerificationState',
    note: { parentPath: '', field: 'acceptanceApprovementNote' },
    noteLabelKey: 'acceptanceApprovementNote',
    changeStateAction: 'updateProjectAcceptanceApprovalState',
    saveAction: 'updateProjectAcceptanceApproval',
  },
  data: {
    blockId: PROJECT_ACCEPTANCE_REPORT_DATA_BLOCK,
    blockField: 'acceptanceReportData',
    labelKey: 'collected_data',
    dataComponent: ProjectAcceptanceReportData,
    requestSectionNumber: 13,
    requestAction: 'requestProjectAcceptanceVerification',
  },
});

export const SERVICE_REPORT = freezeProtocol({
  id: 'service-report',
  groupId: PROJECT_SERVICE_REPORT_TAB_GROUP,
  build: {
    blockId: PROJECT_SERVICE_REPORT_BUILD_BLOCK,
    blockField: 'serviceReportBuild',
    labelKey: 'generate_report',
    pdfField: 'serviceReportAttachments',
    pdfBuildStateField: 'serviceReportAttachmentsBuildState',
    zipAttachmentType: 'serviceReportImageAttachments',
    zipField: 'serviceReportImageAttachmentsZip',
    zipLabelKey: 'serviceReportImageAttachments',
    printRoute: 'ServicePrintPage',
    buildAction: 'buildServiceReport',
    reloadAction: 'fetchProjectServiceReportBuildBlock',
    hidesDownloadsForGuests: true,
  },
  verification: {
    blockId: PROJECT_SERVICE_REPORT_VERIFICATION_BLOCK,
    blockField: 'serviceReportVerification',
    labelKey: 'verification',
    types: SERVICE_VERIFICATION_TYPES,
    stateTypeByType: STATE_TYPE_BY_SERVICE_VERIFICATION_TYPE,
    changeStateAction: 'updateProjectServiceVerificationState',
  },
  approval: {
    blockId: PROJECT_SERVICE_REPORT_APPROVAL_BLOCK,
    blockField: 'serviceReportApproval',
    labelKey: 'approvement',
    saveLabelKey: 'project_service_report_approval',
    stateType: 'serviceApprovementVerificationState',
    note: { parentPath: '', field: 'serviceApprovementNote' },
    noteLabelKey: 'serviceApprovementNote',
    changeStateAction: 'updateProjectServiceApprovalState',
    saveAction: 'updateProjectServiceApproval',
  },
  data: {
    blockId: PROJECT_SERVICE_REPORT_DATA_BLOCK,
    blockField: 'serviceReportData',
    labelKey: 'collected_data',
    dataComponent: ProjectServiceReportData,
    requestSectionNumber: 12,
    requestAction: 'requestProjectServiceVerification',
  },
});

/** The quality tab has no report, no verification and no approval: one data section. */
export const QUALITY_REPORT = freezeProtocol({
  id: 'quality-report',
  groupId: PROJECT_QUALITY_REPORT_TAB_GROUP,
  data: {
    blockId: PROJECT_QUALITY_REPORT_DATA_BLOCK,
    blockField: 'qualityReportData',
    labelKey: 'collected_data',
    dataComponent: ProjectQualityReportData,
  },
});

/**
 * The sales tab has a report and data, no verification and no approval. The PDF lives in the
 * unified attachment list; there is no photo ZIP, so no downloads column.
 */
export const SALES_REPORT = freezeProtocol({
  id: 'sales-report',
  groupId: PROJECT_SALES_REPORT_TAB_GROUP,
  build: {
    blockId: PROJECT_SALES_REPORT_BUILD_BLOCK,
    blockField: 'salesReportBuild',
    labelKey: 'generate_report',
    pdfAttachmentType: 'SALES_REPORT',
    printRoute: 'SalesReportPrintPage',
    buildAction: 'buildSalesReport',
    reloadAction: 'fetchProjectSalesReportBuildBlock',
  },
  data: {
    blockId: PROJECT_SALES_REPORT_DATA_BLOCK,
    blockField: 'salesReportData',
    labelKey: 'collected_data',
    dataComponent: ProjectSalesReportData,
  },
});
